import type { ChangeEvent, ReactNode } from 'react';

export function Card({
  title,
  actions,
  children,
  padded = true,
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
  padded?: boolean;
}) {
  return (
    <section className="card">
      {(title || actions) && (
        <header>
          {title && <h2>{title}</h2>}
          <span className="spacer" />
          {actions}
        </header>
      )}
      {padded ? <div className="body">{children}</div> : children}
    </section>
  );
}

export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && <span className="small muted">{hint}</span>}
    </label>
  );
}

export function TextField({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  hint,
  required,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  hint?: string;
  required?: boolean;
  autoFocus?: boolean;
}) {
  return (
    <Field label={label} hint={hint}>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        required={required}
        autoFocus={autoFocus}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      />
    </Field>
  );
}

export function NumberField({
  label,
  value,
  onChange,
  step = 1,
  min = 0,
  hint,
}: {
  label: string;
  value: number | '';
  onChange: (value: number | '') => void;
  step?: number;
  min?: number;
  hint?: string;
}) {
  return (
    <Field label={label} hint={hint}>
      <input
        type="number"
        step={step}
        min={min}
        value={value}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      />
    </Field>
  );
}

export function SelectField<T extends { id: string }>({
  label,
  value,
  onChange,
  options,
  render,
  placeholder = '— bitte wählen —',
  allowEmpty = true,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: T[];
  render: (option: T) => string;
  placeholder?: string;
  allowEmpty?: boolean;
}) {
  return (
    <Field label={label}>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {allowEmpty && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {render(option)}
          </option>
        ))}
      </select>
    </Field>
  );
}

export function CheckField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="check">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

export function Notice({ kind, children }: { kind: 'error' | 'success' | 'info'; children: ReactNode }) {
  return <div className={`notice ${kind}`}>{children}</div>;
}

export function Badge({ kind = 'default', children }: { kind?: 'default' | 'ok' | 'warn' | 'bad' | 'info'; children: ReactNode }) {
  return <span className={kind === 'default' ? 'badge' : `badge ${kind}`}>{children}</span>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Loading() {
  return <div className="empty">Wird geladen …</div>;
}

const REASON_STYLE: Record<string, 'ok' | 'bad' | 'info' | 'warn' | 'default'> = {
  wareneingang: 'ok',
  retoure: 'ok',
  warenausgang: 'info',
  umlagerung: 'default',
  korrektur: 'warn',
  inventur: 'warn',
};

const REASON_LABEL: Record<string, string> = {
  wareneingang: 'Wareneingang',
  warenausgang: 'Warenausgang',
  umlagerung: 'Umlagerung',
  korrektur: 'Korrektur',
  inventur: 'Inventur',
  retoure: 'Retoure',
};

export function ReasonBadge({ reason }: { reason: string }) {
  return <Badge kind={REASON_STYLE[reason] ?? 'default'}>{REASON_LABEL[reason] ?? reason}</Badge>;
}
